
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface BlogPost {
  id: number;
  title: string;
  category: string;
  image: string;
  content: string;
  date: string;
  author: string;
}

export default function BlogPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postId = searchParams.get('id');
  const [isScrolled, setIsScrolled] = useState(false);

  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const blogPosts: BlogPost[] = [
    {
      id: 1,
      title: '¿Cómo elegir el plan de internet adecuado?',
      category: 'Internet',
      image: 'https://readdy.ai/api/search-image?query=person%20using%20high%20speed%20fiber%20optic%20internet%20connection%20on%20laptop%20at%20home%20with%20wifi%20router%20glowing%20lights%20showing%20fast%20network%20connectivity%20modern%20technology%20setup%20clean%20background&width=1200&height=600&seq=blog001&orientation=landscape',
      date: '15 Enero 2025',
      author: 'TecnosMX',
      content: `
        <h2>Elegir el plan de internet correcto es fundamental</h2>
        <p>En la era digital actual, contar con un servicio de internet confiable y rápido es esencial para el hogar y el negocio. Pero, ¿cómo saber qué plan es el adecuado para ti?</p>
        
        <h3>Factores a considerar:</h3>
        <ul>
          <li><strong>Número de usuarios:</strong> Cuantas más personas conectadas simultáneamente, mayor velocidad necesitarás.</li>
          <li><strong>Tipo de uso:</strong> Streaming en 4K, videojuegos online y videollamadas requieren mayor ancho de banda.</li>
          <li><strong>Dispositivos conectados:</strong> Smartphones, tablets, computadoras, smart TVs, todos consumen ancho de banda.</li>
          <li><strong>Trabajo remoto:</strong> Si trabajas desde casa, necesitas una conexión estable y rápida.</li>
        </ul>

        <h3>Recomendaciones por uso:</h3>
        <p><strong>Uso básico (1-2 usuarios):</strong> Planes de 10-50 Mbps son suficientes para navegación web, redes sociales y correo electrónico.</p>
        <p><strong>Uso medio (3-4 usuarios):</strong> Planes de 100-200 Mbps permiten streaming HD, videollamadas y navegación simultánea.</p>
        <p><strong>Uso intensivo (5+ usuarios):</strong> Planes de 300+ Mbps son ideales para hogares con múltiples dispositivos, gaming y streaming 4K.</p>

        <h3>Ventajas de nuestros planes:</h3>
        <ul>
          <li>Velocidad garantizada sin límite de datos</li>
          <li>Instalación profesional sin costo</li>
          <li>Soporte técnico 24/7</li>
          <li>Sin contratos de permanencia</li>
        </ul>

        <p>En TecnosMX ofrecemos planes desde 10 Mbps hasta 200 Mbps para adaptarnos a todas tus necesidades. Contáctanos y te ayudaremos a elegir el plan perfecto.</p>
      `
    },
    {
      id: 2,
      title: 'Beneficios de las cámaras de seguridad',
      category: 'Seguridad',
      image: 'https://readdy.ai/api/search-image?query=home%20security%20camera%20monitoring%20system%20showing%20multiple%20camera%20views%20on%20screen%20with%20motion%20detection%20alerts%20modern%20surveillance%20technology%20for%20residential%20and%20commercial%20protection%20clean%20background&width=1200&height=600&seq=blog002&orientation=landscape',
      date: '12 Enero 2025',
      author: 'TecnosMX',
      content: `
        <h2>Protege lo que más importa con cámaras de seguridad</h2>
        <p>La seguridad de tu hogar o negocio es una prioridad. Las cámaras de seguridad modernas ofrecen tranquilidad y protección las 24 horas del día.</p>

        <h3>Principales beneficios:</h3>
        <ul>
          <li><strong>Prevención de delitos:</strong> La presencia visible de cámaras disuade a posibles intrusos.</li>
          <li><strong>Monitoreo remoto:</strong> Visualiza tu propiedad desde cualquier lugar mediante tu smartphone.</li>
          <li><strong>Evidencia en video:</strong> Grabaciones que pueden ser útiles en caso de incidentes.</li>
          <li><strong>Alertas en tiempo real:</strong> Notificaciones instantáneas ante movimientos sospechosos.</li>
          <li><strong>Reducción de seguros:</strong> Muchas aseguradoras ofrecen descuentos con sistemas de seguridad.</li>
        </ul>

        <h3>Tipos de cámaras disponibles:</h3>
        <p><strong>Cámaras IP:</strong> Conexión a internet para acceso remoto y almacenamiento en la nube.</p>
        <p><strong>Cámaras con visión nocturna:</strong> Monitoreo efectivo incluso en completa oscuridad.</p>
        <p><strong>Cámaras con detección de movimiento:</strong> Grabación automática al detectar actividad.</p>
        <p><strong>Cámaras PTZ:</strong> Control remoto de panorámica, inclinación y zoom.</p>

        <h3>Instalación profesional:</h3>
        <p>En TecnosMX ofrecemos instalación profesional de cámaras de seguridad con:</p>
        <ul>
          <li>Evaluación del sitio y recomendaciones personalizadas</li>
          <li>Instalación limpia y profesional</li>
          <li>Configuración completa del sistema</li>
          <li>Capacitación en el uso del equipo</li>
          <li>Soporte técnico continuo</li>
        </ul>

        <p>Protege tu patrimonio con la mejor tecnología de seguridad. Contáctanos para una cotización sin compromiso.</p>
      `
    },
    {
      id: 3,
      title: 'Mantenimiento preventivo de computadoras',
      category: 'Soporte',
      image: 'https://readdy.ai/api/search-image?query=computer%20technician%20performing%20maintenance%20and%20cleaning%20on%20desktop%20PC%20tower%20with%20tools%20and%20equipment%20professional%20IT%20support%20service%20hardware%20repair%20and%20optimization%20clean%20background&width=1200&height=600&seq=blog003&orientation=landscape',
      date: '10 Enero 2025',
      author: 'TecnosMX',
      content: `
        <h2>Mantén tu equipo funcionando como nuevo</h2>
        <p>El mantenimiento preventivo de computadoras es esencial para prolongar la vida útil de tu equipo y evitar problemas costosos.</p>

        <h3>¿Por qué es importante el mantenimiento?</h3>
        <ul>
          <li><strong>Mejor rendimiento:</strong> Elimina archivos innecesarios y optimiza el sistema.</li>
          <li><strong>Prevención de fallas:</strong> Detecta problemas antes de que se conviertan en averías graves.</li>
          <li><strong>Mayor vida útil:</strong> Un equipo bien mantenido dura más años.</li>
          <li><strong>Seguridad:</strong> Actualiza software y protege contra virus y malware.</li>
          <li><strong>Ahorro de dinero:</strong> Prevenir es más económico que reparar.</li>
        </ul>

        <h3>Mantenimiento físico:</h3>
        <p><strong>Limpieza interna:</strong> Eliminación de polvo de ventiladores, disipadores y componentes.</p>
        <p><strong>Revisión de conexiones:</strong> Verificación de cables y conectores.</p>
        <p><strong>Aplicación de pasta térmica:</strong> Mejora la disipación de calor del procesador.</p>
        <p><strong>Limpieza externa:</strong> Teclado, pantalla y carcasa.</p>

        <h3>Mantenimiento de software:</h3>
        <ul>
          <li>Actualización del sistema operativo y programas</li>
          <li>Eliminación de archivos temporales y basura</li>
          <li>Desfragmentación del disco duro</li>
          <li>Análisis y eliminación de virus</li>
          <li>Optimización del inicio del sistema</li>
          <li>Respaldo de información importante</li>
        </ul>

        <h3>¿Con qué frecuencia?</h3>
        <p><strong>Uso doméstico:</strong> Mantenimiento cada 6 meses.</p>
        <p><strong>Uso profesional:</strong> Mantenimiento cada 3-4 meses.</p>
        <p><strong>Ambientes polvorientos:</strong> Mantenimiento cada 2-3 meses.</p>

        <h3>Nuestro servicio incluye:</h3>
        <ul>
          <li>Diagnóstico completo del equipo</li>
          <li>Limpieza física profunda</li>
          <li>Optimización de software</li>
          <li>Actualización de drivers y programas</li>
          <li>Informe detallado del estado del equipo</li>
          <li>Recomendaciones personalizadas</li>
        </ul>

        <p>En TecnosMX contamos con técnicos certificados para el mantenimiento de tu equipo. Agenda tu cita hoy mismo.</p>
      `
    },
    {
      id: 4,
      title: 'Tendencias tecnológicas 2025',
      category: 'Tecnología',
      image: 'https://readdy.ai/api/search-image?query=futuristic%20technology%20trends%20including%20artificial%20intelligence%20cloud%20computing%20and%20digital%20innovation%20concepts%20with%20modern%20devices%20and%20holographic%20displays%20clean%20professional%20background&width=1200&height=600&seq=blog004&orientation=landscape',
      date: '8 Enero 2025',
      author: 'TecnosMX',
      content: `
        <h2>El futuro de la tecnología está aquí</h2>
        <p>El 2025 trae consigo avances tecnológicos que transformarán la forma en que vivimos y trabajamos. Conoce las tendencias más importantes.</p>

        <h3>1. Inteligencia Artificial Generativa</h3>
        <p>La IA generativa está revolucionando la creación de contenido, diseño y programación. Herramientas como ChatGPT y DALL-E están siendo adoptadas masivamente en empresas y hogares.</p>
        <ul>
          <li>Asistentes virtuales más inteligentes</li>
          <li>Automatización de tareas repetitivas</li>
          <li>Personalización de experiencias</li>
          <li>Análisis predictivo avanzado</li>
        </ul>

        <h3>2. Internet de las Cosas (IoT)</h3>
        <p>Cada vez más dispositivos están conectados a internet, creando hogares y ciudades inteligentes.</p>
        <ul>
          <li>Electrodomésticos inteligentes</li>
          <li>Sistemas de seguridad conectados</li>
          <li>Iluminación y climatización automatizada</li>
          <li>Monitoreo de salud en tiempo real</li>
        </ul>

        <h3>3. Computación en la Nube</h3>
        <p>El almacenamiento y procesamiento en la nube continúa creciendo, ofreciendo flexibilidad y escalabilidad.</p>
        <ul>
          <li>Acceso a archivos desde cualquier lugar</li>
          <li>Colaboración en tiempo real</li>
          <li>Reducción de costos en infraestructura</li>
          <li>Mayor seguridad de datos</li>
        </ul>

        <h3>4. Ciberseguridad Avanzada</h3>
        <p>Con el aumento de amenazas digitales, la seguridad es más importante que nunca.</p>
        <ul>
          <li>Autenticación biométrica</li>
          <li>Encriptación de extremo a extremo</li>
          <li>Detección de amenazas con IA</li>
          <li>Protección de identidad digital</li>
        </ul>

        <h3>5. Conectividad 5G y Fibra Óptica</h3>
        <p>Las redes de alta velocidad permiten nuevas aplicaciones y servicios.</p>
        <ul>
          <li>Streaming 8K sin interrupciones</li>
          <li>Realidad virtual y aumentada fluida</li>
          <li>Telemedicina de alta calidad</li>
          <li>Trabajo remoto sin limitaciones</li>
        </ul>

        <h3>6. Sostenibilidad Tecnológica</h3>
        <p>La tecnología verde está en auge, con dispositivos más eficientes energéticamente.</p>
        <ul>
          <li>Equipos con menor consumo energético</li>
          <li>Reciclaje de componentes electrónicos</li>
          <li>Centros de datos ecológicos</li>
          <li>Energías renovables en tecnología</li>
        </ul>

        <h3>¿Cómo prepararse?</h3>
        <p>En TecnosMX te ayudamos a estar a la vanguardia tecnológica:</p>
        <ul>
          <li>Asesoría en adopción de nuevas tecnologías</li>
          <li>Instalación de sistemas inteligentes</li>
          <li>Capacitación en herramientas digitales</li>
          <li>Soporte técnico especializado</li>
        </ul>

        <p>El futuro es ahora. Contáctanos y descubre cómo la tecnología puede mejorar tu vida y negocio.</p>
      `
    }
  ];

  const currentPost = blogPosts.find(post => post.id === Number(postId));

  if (!currentPost) {
    return (
      <div className="min-h-screen bg-white">
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                <img src="https://static.readdy.ai/image/fcd41dfa5df98c2f0a8b6825ec9e25ef/890511506ba52d1a941f8ddafad7ce2c.png" alt="TecnosMX Logo" className="h-12 w-auto" />
              </div>
              <button 
                onClick={() => navigate('/')}
                className="text-sm font-medium text-gray-700 hover:text-[#2B5BA6] transition-colors whitespace-nowrap cursor-pointer"
              >
                Volver al inicio
              </button>
            </div>
          </div>
        </nav>

        <div className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Artículo no encontrado</h1>
            <p className="text-lg text-gray-600 mb-8">El artículo que buscas no existe.</p>
            <button 
              onClick={() => navigate('/')}
              className="bg-[#2B5BA6] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#234a8a] transition-colors whitespace-nowrap cursor-pointer"
            >
              Volver al Blog
            </button>
          </div>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts.filter(post => post.id !== currentPost.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="https://static.readdy.ai/image/fcd41dfa5df98c2f0a8b6825ec9e25ef/890511506ba52d1a941f8ddafad7ce2c.png" alt="TecnosMX Logo" className="h-12 w-auto" />
            </div>
            
            <div className="flex items-center gap-6">
              <button 
                onClick={() => navigate('/')}
                className="text-sm font-medium text-gray-700 hover:text-[#2B5BA6] transition-colors whitespace-nowrap cursor-pointer"
              >
                Inicio
              </button>
              <a 
                href="https://wa.me/5219541397099" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white px-5 py-2 rounded-full text-sm font-medium flex items-center gap-2 hover:bg-[#20BD5A] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <div className="pt-32 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#2B5BA6] font-medium mb-8 hover:gap-3 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Volver al Blog
          </button>

          <span className="inline-block bg-[#2B5BA6] text-white text-sm px-4 py-2 rounded-full mb-6">
            {currentPost.category}
          </span>

          <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {currentPost.title}
          </h1>

          <div className="flex items-center gap-6 text-gray-600 mb-8">
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line"></i>
              <span>{currentPost.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-user-line"></i>
              <span>{currentPost.author}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      <div className="mb-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="w-full h-96 rounded-3xl overflow-hidden">
            <img 
              src={currentPost.image} 
              alt={currentPost.title} 
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div 
            className="prose prose-lg max-w-none"
            style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151'
            }}
            dangerouslySetInnerHTML={{ __html: currentPost.content }}
          />

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-br from-[#4A90E2] to-[#2B5BA6] rounded-3xl p-12 text-center">
            <h3 className="text-3xl font-bold text-white mb-4">
              ¿Necesitas ayuda con tecnología?
            </h3>
            <p className="text-white/90 text-lg mb-8">
              Nuestro equipo está listo para ayudarte con cualquier consulta o servicio
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href="https://wa.me/5219541397099" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white text-[#2B5BA6] px-8 py-4 rounded-full font-semibold flex items-center gap-2 hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Contactar por WhatsApp
              </a>
              <button 
                onClick={() => navigate('/')}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-[#2B5BA6] transition-colors whitespace-nowrap cursor-pointer"
              >
                Ver Servicios
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-12">
              Artículos Relacionados
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map(post => (
                <div 
                  key={post.id} 
                  onClick={() => navigate(`/blog?id=${post.id}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                >
                  <div className="w-full h-48">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-6">
                    <span className="inline-block bg-[#2B5BA6] text-white text-xs px-3 py-1 rounded-full mb-3">
                      {post.category}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">{post.title}</h3>
                    <button className="text-[#2B5BA6] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                      Leer más
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-[#1a2332] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <img src="https://static.readdy.ai/image/fcd41dfa5df98c2f0a8b6825ec9e25ef/890511506ba52d1a941f8ddafad7ce2c.png" alt="TecnosMX" className="h-12 mb-6" />
              <p className="text-white/70 mb-6">
                Tecnología completa y papelería profesional en dos locales para servirte mejor
              </p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Contacto</h4>
              <ul className="space-y-2">
                <li><a href="tel:+5219541397099" className="text-white/70 hover:text-white transition-colors cursor-pointer">+52 954 139 7099</a></li>
                <li><a href="tel:+5219541417890" className="text-white/70 hover:text-white transition-colors cursor-pointer">+52 954 141 7890</a></li>
                <li><a href="mailto:tecnosjuquila16@gmail.com" className="text-white/70 hover:text-white transition-colors cursor-pointer">tecnosjuquila16@gmail.com</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Síguenos</h4>
              <div className="flex items-center gap-4">
                <a href="#" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="https://wa.me/5219541397099" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                  <i className="ri-whatsapp-line"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/70 text-sm">© 2025 TecnosMX. Todos los derechos reservados</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/70 text-sm hover:text-white transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      <style>{`
        .prose h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin-top: 2.5rem;
          margin-bottom: 1.5rem;
        }
        .prose h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .prose p {
          margin-bottom: 1.5rem;
        }
        .prose ul {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        .prose li {
          margin-bottom: 0.75rem;
          list-style-type: disc;
        }
        .prose strong {
          color: #2B5BA6;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}
